import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

type AdminCheck = 'checking' | 'admin' | 'unauthorized';

/**
 * admin_users sözleşmesi: 'admin_users' tablosunda oturum sahibinin
 * user_id'si ile eşleşen >=1 satır varsa admin sayılır.
 * select('user_id').eq('user_id', session.user.id)
 */
function useAdminCheck(userId: string | undefined) {
  const [status, setStatus] = useState<AdminCheck>('checking');

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    supabase
      .from('admin_users')
      .select('user_id')
      .eq('user_id', userId)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setStatus('unauthorized');
          return;
        }
        setStatus((data?.length ?? 0) >= 1 ? 'admin' : 'unauthorized');
      });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  return status;
}

function UnauthorizedScreen() {
  const { signOut } = useAuth();
  const { t } = useTranslation();
  return (
    <div className="centered-screen">
      <h2>{t('unauthorized.title')}</h2>
      <p>{t('unauthorized.body')}</p>
      <button onClick={() => void signOut()}>{t('unauthorized.signOut')}</button>
    </div>
  );
}

export function RequireAdmin({ children }: { children: ReactNode }) {
  const { session, loading: authLoading } = useAuth();
  const adminStatus = useAdminCheck(session?.user.id);
  const { t } = useTranslation();

  if (authLoading) return <div className="centered-screen">{t('common.loading')}</div>;
  if (!session) return <Navigate to="/login" replace />;
  if (adminStatus === 'checking') return <div className="centered-screen">{t('common.loading')}</div>;
  if (adminStatus === 'unauthorized') return <UnauthorizedScreen />;

  return <>{children}</>;
}
